import { useEffect } from 'react';
import { useAuth,useUser,SignIn, SignUp } from '@clerk/clerk-react';
import App from './App';
import Admin from './leads/Admin';
import LeadResearch from './leads/LeadResearch';

// Simple path-based router
function useRoute() {
  const path = window.location.pathname;
  if (path.startsWith('/admin')) return 'admin';
  if (path.startsWith('/leads')) return 'leads';
  if (path.startsWith('/sign-in')) return 'sign-in';
  if (path.startsWith('/sign-up')) return 'sign-up';
  return 'home';
}

// Protected wrapper — redirects to sign-in if not authenticated
function Protected({ children }) {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const { user } = useUser();

  useEffect(() => {
    if (isLoaded && !isSignedIn) {
      window.location.href = '/sign-in';
    }
  }, [isLoaded, isSignedIn]);

  // Sync user to Supabase on every login
  useEffect(() => {
    if (isLoaded && isSignedIn && user) {
      getToken().then(token => {
        fetch('https://api.rakaba.io/api/auth/sync', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': process.env.REACT_APP_API_KEY,
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            email: user.primaryEmailAddress?.emailAddress,
            full_name: user.fullName,
            avatar_url: user.imageUrl
          })
        }).catch(err => console.error('[sync]', err));
      });
    }
  }, [isLoaded, isSignedIn, user, getToken]);

  if (!isLoaded) return <PageLoader />;
  if (!isSignedIn) return <PageLoader />;
  return children;
}

function PageLoader() {
  return (
    <div style={{
      minHeight: '100vh',
      background: '#080c10',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      gap: '16px'
    }}>
      <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
        <ellipse cx="13" cy="13" rx="7" ry="10" transform="rotate(-45 13 13)" fill="#10b981" opacity="0.95"/>
        <ellipse cx="23" cy="13" rx="7" ry="10" transform="rotate(45 23 13)" fill="#10b981" opacity="0.75"/>
        <ellipse cx="13" cy="23" rx="7" ry="10" transform="rotate(45 13 23)" fill="#10b981" opacity="0.75"/>
        <ellipse cx="23" cy="23" rx="7" ry="10" transform="rotate(-45 23 23)" fill="#10b981" opacity="0.55"/>
        <circle cx="18" cy="18" r="3.5" fill="#052e16"/>
      </svg>
      <div style={{ color: '#4a5568', fontSize: '13px', fontFamily: 'monospace' }}>Loading Rakaba...</div>
    </div>
  );
}

function AuthPage({ mode }) {
  const { isLoaded, isSignedIn } = useAuth();

  useEffect(() => {
    if (isLoaded && isSignedIn) {
      window.location.href = '/leads';
    }
  }, [isLoaded, isSignedIn]);

  if (!isLoaded) return <PageLoader />;

  return (
    <div style={{
      minHeight: '100vh',
      background: '#080c10',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      gap: '2rem'
    }}>
      {/* Rakaba Logo */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <svg width="32" height="32" viewBox="0 0 36 36" fill="none">
          <ellipse cx="13" cy="13" rx="7" ry="10" transform="rotate(-45 13 13)" fill="#10b981" opacity="0.95"/>
          <ellipse cx="23" cy="13" rx="7" ry="10" transform="rotate(45 23 13)" fill="#10b981" opacity="0.75"/>
          <ellipse cx="13" cy="23" rx="7" ry="10" transform="rotate(45 13 23)" fill="#10b981" opacity="0.75"/>
          <ellipse cx="23" cy="23" rx="7" ry="10" transform="rotate(-45 23 23)" fill="#10b981" opacity="0.55"/>
          <circle cx="18" cy="18" r="3.5" fill="#052e16"/>
          <line x1="18" y1="21.5" x2="18" y2="30" stroke="#10b981" strokeWidth="1.8" strokeLinecap="round"/>
        </svg>
        <span style={{
          fontFamily: "'Syne', sans-serif",
          fontWeight: 700,
          fontSize: '22px',
          color: '#e8edf2',
          letterSpacing: '-0.02em'
        }}>
          Rakaba
        </span>
      </div>

      {mode === 'sign-in'
        ? <SignIn
            routing="path"
            path="/sign-in"
            forceRedirectUrl="/leads"
            signUpUrl="/sign-up"
          />
        : <SignUp
            routing="path"
            path="/sign-up"
            forceRedirectUrl="/leads"
            signInUrl="/sign-in"
          />
      }
    </div>
  );
}

export default function AppRouter() {
  const route = useRoute();

  if (route === 'admin') return <Protected><Admin /></Protected>;
  if (route === 'sign-in') return <AuthPage mode="sign-in" />;
  if (route === 'sign-up') return <AuthPage mode="sign-up" />;

  if (route === 'leads') {
    return (
      <Protected>
        <LeadResearch />
      </Protected>
    );
  }

  // Home route — chat assistant, protected
  return (
    <Protected>
      <App />
    </Protected>
  );
}
